import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, Icon, IconButton, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ConsignmentImportRepository } from "../../repositories/ConsignmentImportRepository";

const organizationLabel = (organization) => {
  const name = organization?.nameEn || organization?.name || organization?.id || "";
  const country = typeof organization?.country === "string" ? organization.country : organization?.country?.nameEn || organization?.country?.name;
  return country ? `${name} (${country})` : name;
};

const active = (job) => ["QUEUED", "PROCESSING", "SAVING_DRAFT"].includes(job.status);
const labels = { QUEUED: "Queued", PROCESSING: "Processing", SAVING_DRAFT: "Saving draft", COMPLETED: "Completed", FAILED: "Failed" };
const message = (error) => {
  if (error?.response?.status === 429) return "Draft creation is temporarily unavailable. Please try again later or contact support.";
  const detail = error?.response?.data?.message;
  return detail && !/gemini|google|vertex|token|quota|ocr|api|model/i.test(detail) ? detail : "Unable to complete this request. Please try again or contact support.";
};

export default function ConsignmentImports({ organizations = [], client = false, onCompleted, uploadRequest, hideUploadButton = false, fixedOrganization, onReviewDraft }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  useEffect(() => {
    if (!uploadRequest) return;
    setSubmittedId(null); setUploadError(""); setOpen(true);
  }, [uploadRequest]);
  const [dragging, setDragging] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [notice, setNotice] = useState("");
  const [jobsLoading, setJobsLoading] = useState(true);
  const fullScreen = useMediaQuery("(max-width:600px)");
  const [limits, setLimits] = useState(null);
  const [limitsError, setLimitsError] = useState("");
  const [limitsRefresh, setLimitsRefresh] = useState(0);
  const quotaReached = limits?.available === false;
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLimits(null); setLimitsError("");
    ConsignmentImportRepository.limits().then((response) => { if (!cancelled) setLimits(response.data); }).catch((error) => { if (!cancelled) setLimitsError(message(error)); });
    return () => { cancelled = true; };
  }, [open, limitsRefresh]);
  const [page, setPage] = useState(0);
  const [files, setFiles] = useState([]);
  const [organization, setOrganization] = useState(fixedOrganization ?? null);
  const [organizationInput, setOrganizationInput] = useState("");
  const [organizationOptions, setOrganizationOptions] = useState(null);
  const [searchingOrganizations, setSearchingOrganizations] = useState(false);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const completed = useRef(new Map());
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

  useEffect(() => {
    if (client || fixedOrganization || !open || organizationInput.trim().length < 3) {
      setOrganizationOptions(null); setSearchingOrganizations(false); return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearchingOrganizations(true);
      try {
        const response = await ConsignmentImportRepository.organizations(organizationInput.trim());
        if (!cancelled) setOrganizationOptions(response.data);
      } catch (e) { if (!cancelled) setUploadError(message(e)); }
      finally { if (!cancelled) setSearchingOrganizations(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [organizationInput, client, open, fixedOrganization]);

  useEffect(() => {
    let mounted = true;
    let timer;
    const poll = async () => {
      try {
        const response = await ConsignmentImportRepository.list(page);
        if (!mounted) return;
        const rows = response.data;
        let newlyCompleted = false;
        rows.forEach((job) => {
          if (completed.current.has(job.id) && completed.current.get(job.id) !== "COMPLETED" && job.status === "COMPLETED") newlyCompleted = true;
          completed.current.set(job.id, job.status);
        });
        setJobs(rows);
        setError("");
        if (newlyCompleted) onCompletedRef.current?.();
      } catch (e) { if (mounted) setError(message(e)); }
      finally { if (mounted) { setJobsLoading(false); timer = setTimeout(poll, 5000); } }
    };
    poll();
    return () => { mounted = false; clearTimeout(timer); };
  }, [page, refresh]);

  const addFiles = (selected) => {
    if (uploading || !limits || quotaReached) return;
    const combined = [...files, ...selected];
    if (combined.some((file) => !/\.(pdf|jpe?g|png)$/i.test(file.name) || !file.size)) {
      setUploadError("Select non-empty PDF, JPEG or PNG files."); return;
    }
    if (limits && (combined.length > limits.maxFiles || combined.reduce((sum, file) => sum + file.size, 0) > limits.maxBytes)) {
      setUploadError(`Select up to ${limits.maxFiles} files, totaling no more than ${limits.maxBytes / 1024 / 1024} MB.`); return;
    }
    if (selected.some((file) => files.some((existing) => existing.name === file.name && existing.size === file.size && existing.lastModified === file.lastModified))) {
      setUploadError("That file is already selected. Remove duplicate scans before uploading."); return;
    }
    setFiles(combined); setUploadError("");
  };

  const submit = async () => {
    if (!limits || quotaReached || !files.length || (!client && !organization)) return;
    setUploading(true); setUploadError(""); setUploadProgress(0);
    try {
      const response = await ConsignmentImportRepository.upload(files, (fixedOrganization ?? organization)?.id, (event) => {
        if (event.total) setUploadProgress(Math.round(event.loaded * 100 / event.total));
      });
      completed.current.set(response.data.id, response.data.status);
      setFiles([]); setPage(0); setSubmittedId(response.data.id);
      setJobs((previous) => [response.data, ...previous.filter((job) => job.id !== response.data.id)]);
      setNotice("Upload received. Processing continues in the background. Open the draft when it is ready.");
      setLimitsRefresh((value) => value + 1); setRefresh((value) => value + 1);
    } catch (e) { setUploadError(message(e)); }
    finally { setUploading(false); }
  };

  const retry = async (job) => {
    setBusyId(job.id);
    try {
      const response = await ConsignmentImportRepository.retry(job.id);
      setJobs((previous) => previous.map((item) => item.id === job.id ? response.data : item));
      setRefresh((value) => value + 1); setLimitsRefresh((value) => value + 1);
    }
    catch (e) { setError(message(e)); }
    finally { setBusyId(null); }
  };

  const downloadOriginal = async (job, position) => {
    setBusyId(job.id);
    setError("");
    try {
      const response = await ConsignmentImportRepository.originalFile(job.id, position);
      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a"); link.href = href; link.download = job.files[position];
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(href);
    } catch (e) { setError("Unable to download the uploaded file. Please try again."); }
    finally { setBusyId(null); }
  };

  const moveFile = (index, direction) => setFiles((previous) => {
    const reordered = [...previous];
    [reordered[index], reordered[index + direction]] = [reordered[index + direction], reordered[index]];
    return reordered;
  });
  const close = () => { if (!uploading) { setOpen(false); setSubmittedId(null); } };
  const activeCount = jobs.filter(active).length;
  const canUpload = !uploading && limits && !quotaReached && files.length > 0 && files.length <= limits.maxFiles && totalBytes <= limits.maxBytes && (client || organization);

  const reviewDraft = async (job) => {
    if (busyId) return;
    setBusyId(job.id); setError("");
    try {
      if (onReviewDraft) await onReviewDraft(job.consignmentId);
      else navigate(`${client ? "/client" : "/agents"}/consignment/0/15/%7B%7D/dateCreated/DESC/${job.consignmentId}`);
      setOpen(false); setHistoryOpen(false); setSubmittedId(null);
    } catch (error) { setError(message(error)); }
    finally { setBusyId(null); }
  };

  const submitted = jobs.find((job) => job.id === submittedId);
  useEffect(() => {
    if (onReviewDraft || !open || submitted?.status !== "COMPLETED" || !submitted.consignmentId) return;
    setOpen(false);
    setSubmittedId(null);
    navigate(`${client ? "/client" : "/agents"}/consignment/0/15/%7B%7D/dateCreated/DESC/${submitted.consignmentId}`);
  }, [open, submitted, navigate, client, onReviewDraft]);


  return <Box sx={{ mb: 2 }}>
    <Stack direction="row" spacing={1} justifyContent="flex-end">
    {!hideUploadButton && <Button variant="outlined" startIcon={<Icon>upload_file</Icon>} onClick={() => { setSubmittedId(null); setUploadError(""); setOpen(true); }}>
      Create from PDF / scan{activeCount > 0 && <Chip size="small" color="info" label={`${activeCount} processing`} sx={{ ml: 1 }} />}
    </Button>}
    <Button startIcon={<Icon>history</Icon>} onClick={() => { setPage(0); setRefresh((value) => value + 1); setHistoryOpen(true); }}>Recent imports</Button>
    </Stack>
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" fullScreen={fullScreen} aria-labelledby="declaration-imports-title">
      <DialogTitle id="declaration-imports-title" sx={{ pr: 7 }}>
        Create from PDF / scan
        <Typography variant="body2" color="text.secondary">Turn your PDFs and scans into drafts you can review and edit.</Typography>
        <IconButton aria-label="Close declaration imports" disabled={uploading} onClick={close} sx={{ position: "absolute", right: 12, top: 12 }}><Icon>close</Icon></IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "background.default", p: { xs: 2, sm: 3 }, minHeight: 340 }}>
        {!submittedId ? <Box>
          <Typography sx={{ mb: 2 }}>Add all declaration pages for one truck. Each declaration becomes a separate house in the draft.</Typography>
          {limitsError && <Alert severity="error" sx={{ mb: 2 }}>{limitsError} <Button onClick={() => setLimitsRefresh((value) => value + 1)}>Try again</Button></Alert>}
          {!limits && !limitsError && <LinearProgress sx={{ mb: 2 }} aria-label="Loading import limits" />}
          {quotaReached && <Alert severity="warning" sx={{ mb: 2 }}>Draft creation is temporarily unavailable. Please try again later or contact support.</Alert>}
          {!client && !fixedOrganization && <Autocomplete value={organization} options={organizationOptions ?? organizations} getOptionLabel={organizationLabel}
            loading={searchingOrganizations} filterOptions={(options) => options} isOptionEqualToValue={(a, b) => a.id === b.id} disabled={uploading}
            onChange={(_, value) => { setOrganization(value); setOrganizationInput(organizationLabel(value)); }} inputValue={organizationInput}
            onInputChange={(_, value, reason) => { if (reason === "input" || reason === "clear") { setOrganizationInput(value); if (reason === "input") setOrganization(null); } }}
            renderInput={(params) => <TextField {...params} label="Organization" helperText="Choose the organization for this draft. Type at least 3 characters to search." required sx={{ mb: 3 }} />} />}
          <Paper variant="outlined" onDragOver={(event) => { event.preventDefault(); if (!uploading && limits && !quotaReached) setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(Array.from(event.dataTransfer.files || [])); }}
            sx={{ p: 3, textAlign: "center", border: "2px dashed", borderColor: dragging ? "primary.main" : "divider", bgcolor: dragging ? "action.hover" : "background.paper", borderRadius: 2 }}>
            <Icon color="primary" sx={{ fontSize: 38, mb: 1 }}>upload_file</Icon>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Drop declaration files here</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>PDF, JPEG or PNG — include every page for the same truck.</Typography>
            <Button component="label" variant="contained" disabled={uploading || !limits || quotaReached}>Add files
              <input hidden type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={(event) => { addFiles(Array.from(event.target.files || [])); event.target.value = ""; }} />
            </Button>
            {limits && <Typography variant="caption" display="block" sx={{ mt: 2 }}>Up to {limits.maxFiles} files · {limits.maxBytes / 1024 / 1024} MB combined · {limits.maxPages} pages</Typography>}
          </Paper>
          {files.length > 0 && <Box sx={{ mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="subtitle2">Selected files ({files.length})</Typography><Typography variant="caption">{(totalBytes / 1024 / 1024).toFixed(2)} MB total</Typography></Stack>
            <Typography component="details" onToggle={(event) => setOrdering(event.currentTarget.open)} variant="caption" color="text.secondary"><summary>File order</summary>Files are processed in the order shown. Use the arrows if you need to change it.</Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>{files.map((file, index) => <Paper variant="outlined" key={`${file.name}-${index}`} sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip size="small" label={index + 1} /><Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{file.name}</Typography><Typography variant="caption" color="text.secondary">{file.name.toLowerCase().endsWith(".pdf") ? "PDF document" : "Scanned image"} · {(file.size / 1024).toFixed(0)} KB</Typography></Box>
                {ordering && <IconButton size="small" aria-label={`Move ${file.name} up`} disabled={uploading || index === 0} onClick={() => moveFile(index, -1)}><Icon fontSize="small">arrow_upward</Icon></IconButton>}
                {ordering && <IconButton size="small" aria-label={`Move ${file.name} down`} disabled={uploading || index === files.length - 1} onClick={() => moveFile(index, 1)}><Icon fontSize="small">arrow_downward</Icon></IconButton>}
                <IconButton size="small" aria-label={`Remove ${file.name}`} disabled={uploading} onClick={() => setFiles((previous) => previous.filter((_, i) => i !== index))}><Icon fontSize="small">delete_outline</Icon></IconButton>
              </Stack>
            </Paper>)}</Stack>
          </Box>}
          {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}
          {uploading && <Box sx={{ mt: 2 }}><LinearProgress variant="determinate" value={uploadProgress} /><Typography variant="caption" role="status">{uploadProgress === 100 ? "Saving upload…" : `Uploading ${uploadProgress}%`}</Typography></Box>}
        </Box> : <Box>
          <Typography variant="h6">{submitted?.status === "COMPLETED" ? "Your draft is ready" : submitted?.status === "FAILED" ? "Draft creation failed" : "Creating your draft"}</Typography>
          {(!submitted || active(submitted)) && <LinearProgress sx={{ my: 2 }} />}
          <Typography sx={{ my: 2 }}>{onReviewDraft ? "When ready, choose Review draft to add it to this ticket. You can also find it in Recent imports." : "Your draft will open automatically when ready. Close this dialog to continue in the background, then find your draft in Recent imports."}</Typography>
          {submitted?.status === "COMPLETED" && <Button variant="contained" disabled={!!busyId} onClick={() => reviewDraft(submitted)}>Review draft</Button>}
          {submitted?.status === "FAILED" && <Button disabled={busyId === submitted.id} onClick={() => retry(submitted)}>Retry</Button>}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={uploading} onClick={close}>Close</Button>
        {!submittedId && <Button variant="contained" disabled={!canUpload} onClick={submit}>{uploading ? "Uploading…" : "Create draft"}</Button>}
      </DialogActions>
    </Dialog>
    <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="lg" fullScreen={fullScreen} aria-labelledby="recent-imports-title">
      <DialogTitle id="recent-imports-title" sx={{ pr: 7 }}>Recent imports
        <IconButton aria-label="Close recent imports" onClick={() => setHistoryOpen(false)} sx={{ position: "absolute", right: 12, top: 12 }}><Icon>close</Icon></IconButton>
      </DialogTitle>
      <DialogContent>
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Imports continue in the background. This list updates automatically.</Typography>
          <Button startIcon={<Icon>refresh</Icon>} onClick={() => setRefresh((value) => value + 1)}>Refresh</Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {jobsLoading && <LinearProgress aria-label="Loading import history" />}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Recent imports" sx={{ "& .MuiTableCell-root": { py: 0.75, px: 1.5, fontSize: "0.8125rem" }, "& .MuiTableCell-head": { fontWeight: 600, bgcolor: "action.hover" } }}>
            <TableHead><TableRow><TableCell>Files</TableCell><TableCell>Uploaded</TableCell><TableCell>Created by</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {!jobsLoading && !error && jobs.length === 0 && <TableRow><TableCell colSpan={5} align="center">No imports yet. Upload a declaration to create your first draft.</TableCell></TableRow>}
              {jobs.map((job) => <TableRow hover key={job.id} sx={{ verticalAlign: "middle" }}>
                <TableCell sx={{ minWidth: 180, maxWidth: 320 }}>
                  {job.files?.length ? job.files.map((name, index) => <Stack key={index} direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" title={name} noWrap sx={{ fontSize: "inherit", minWidth: 0 }}>{name}</Typography>
                    <IconButton size="small" title={`Download ${name}`} aria-label={`Download ${name}`} disabled={busyId === job.id} onClick={() => downloadOriginal(job, index)} sx={{ p: 0.5, flexShrink: 0 }}><Icon sx={{ fontSize: 17 }}>download</Icon></IconButton>
                  </Stack>) : "Declaration import"}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{job.dateCreated ? moment(job.dateCreated).format("DD.MM.YYYY HH:mm") : "Just uploaded"}</TableCell>
                <TableCell>{job.createdBy || "Unknown user"}</TableCell>
                <TableCell sx={{ minWidth: 130, maxWidth: 240 }}>
                  <Chip size="small" label={job.status === "COMPLETED" ? "Draft ready" : labels[job.status] || job.status} color={job.status === "COMPLETED" ? "success" : job.status === "FAILED" ? "error" : "info"} sx={{ height: 22, fontSize: "0.75rem" }} />
                  {active(job) && <LinearProgress sx={{ mt: 0.5, height: 3 }} aria-label="Creating draft" />}
                  {job.status === "FAILED" && <Typography variant="caption" color="error" display="block">Draft creation failed. Please retry or contact support.</Typography>}
                  {job.warnings?.length > 0 && <Box component="details" sx={{ mt: 1 }}>
                    <Typography component="summary" variant="caption" sx={{ cursor: "pointer" }}>Review warnings ({job.warnings.length})</Typography>
                    {job.warnings.map((warning, index) => <Typography key={index} variant="caption" display="block">{/gemini|google|vertex|token|quota|api|model/i.test(warning) ? "Some details could not be extracted. Please review the draft." : warning}</Typography>)}
                  </Box>}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {job.status === "COMPLETED" && job.consignmentId && <Button size="small" variant="text" disabled={!!busyId} onClick={() => reviewDraft(job)}>Review draft</Button>}
                  {job.status === "FAILED" && <Button size="small" disabled={busyId === job.id} onClick={() => retry(job)}>Retry</Button>}
                  {busyId === job.id && <CircularProgress size={20} />}
                </TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
        {(page > 0 || jobs.length >= 15) && <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <Button disabled={page === 0} onClick={() => setPage((value) => value - 1)}>Previous</Button><Typography variant="caption">Page {page + 1}</Typography><Button disabled={jobs.length < 15} onClick={() => setPage((value) => value + 1)}>Next</Button>
        </Stack>}
      </DialogContent>
      <DialogActions><Button onClick={() => setHistoryOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  </Box>;
}
